import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";

axios.defaults.baseURL = "/api";
axios.defaults.withCredentials = true;
// ALL Axios requests will go to /api/... automatically

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const inactivityTimer = useRef(null);
  const INACTIVITY_LIMIT = 8 * 60 * 60 * 1000; // 8 hours

  // ---------------------
  // Fetch current session
  // ---------------------
  const fetchSession = async () => {
    try {
      const res = await axios.get("/session");
      if (res.data.user) {
        setUser(res.data.user);
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error("Session fetch error:", err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------
  // Login
  // ---------------------
  const login = async (credentials) => {
    try {
      await axios.post("/login", credentials);
      await fetchSession();
    } catch (err) {
      console.error("Login failed:", err);
      throw err;
    }
  };

  // ---------------------
  // Logout
  // ---------------------
  const logout = async () => {
    setUser(null);  // clear immediately so AuthRoutes sees null before navigation
    try {
      await axios.post("/logout");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  // ---------------------
  // Inactivity timer
  // ---------------------
  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer.current);
    if (user) {
      inactivityTimer.current = setTimeout(() => logout(), INACTIVITY_LIMIT);
    }
  };

  const setupInactivityListeners = () => {
    ["mousemove", "keydown", "click", "scroll"]
      .forEach(event => window.addEventListener(event, resetInactivityTimer));
  };

  const removeInactivityListeners = () => {
    ["mousemove", "keydown", "click", "scroll"]
      .forEach(event => window.removeEventListener(event, resetInactivityTimer));
  };

  // On mount
  useEffect(() => {
    fetchSession();
  }, []);

  // User state change
  useEffect(() => {
    if (user) {
      resetInactivityTimer();
      setupInactivityListeners();
    } else {
      clearTimeout(inactivityTimer.current);
      removeInactivityListeners();
    }

    return () => {
      clearTimeout(inactivityTimer.current);
      removeInactivityListeners();
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
